/**
 * Adding for you: what a run pressed Add on (`GET /api/offers/capture/adds`),
 * and the brands added from the Offers page that make an offer worth adding
 * (`/api/offers/capture/interests`). The server decides what a run adds and
 * whether a card's next read confirmed it; this module only fetches and files.
 */
import { authedFetch } from "./api";
import { orThrow } from "./http";

/** Pressed by a run; confirmed or not by the card's next read, or vanished:
 * gone from the card's list, so nobody can say whether it took. */
export type AddState = "pressed" | "confirmed" | "unconfirmed" | "vanished";

/** One offer a run added, once per issuer offer, with each card it went on. */
export interface AddedOffer {
  /** Dismisses it from Refresh and the Offers page (`dismissed_changes`). */
  key: string;
  issuer: string | null;
  merchant: string;
  terms: string | null;
  expires_on: string | null;
  /** `gave_up`: the plan no longer presses it, so it is added on the issuer's site. */
  cards: { card_name: string; state: AddState; failures: number; gave_up: boolean }[];
  /** The Offers page's list only: the tracked offers it became, by id, for the pin route. */
  offer_ids?: number[];
  /** Pinned on any card. A pinned add stays listed though dismissed. */
  pinned?: boolean;
  /** Dismissed already; listed only because it is pinned. */
  dismissed?: boolean;
}

/** Added and confirmed on every card it went on. */
export const allConfirmed = (added: AddedOffer): boolean =>
  added.cards.every((c) => c.state === "confirmed");

const BASE = "/api/offers/capture";

export async function fetchRunAdds(runIds: string[]): Promise<AddedOffer[]> {
  const res = await authedFetch(
    `${BASE}/adds?run_ids=${encodeURIComponent(runIds.join(","))}`, { cache: "no-store" });
  const body = (await orThrow(res, "Could not load what the run added")) as { added: AddedOffer[] };
  return body.added;
}

export interface Interests {
  /** offer_interests.yaml's brands, which only a deploy changes. */
  file: string[];
  added: { merchant: string; added_on: string }[];
}

export async function fetchInterests(): Promise<Interests> {
  const res = await authedFetch(`${BASE}/interests`, { cache: "no-store" });
  return (await orThrow(res, "Could not load offer interests")) as Interests;
}

async function postMerchant(path: string, merchant: string, failure: string): Promise<unknown> {
  const res = await authedFetch(`${BASE}/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ merchant }),
  });
  return orThrow(res, failure);
}

/** Treat future offers from this brand as worth adding, on every issuer. A
 * refusal (a digit in the name, say) throws the server's own reason. */
export async function addInterest(merchant: string): Promise<void> {
  await postMerchant("interests/merchants", merchant, "Could not add that brand");
}

export async function removeInterest(merchant: string): Promise<void> {
  await postMerchant("interests/merchants/remove", merchant, "Could not remove that brand");
}
